import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Typography,
  Box,
  Chip,
  Alert,
  Snackbar,
  Alert as MuiAlert,
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import {
  TrendingUp as TrendingIcon,
  Star as StarIcon,
  Code as CodeIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { keyframes } from '@mui/material';
import apiService, { Repository, InsightsResponse } from '../../services/api';
import AnimatedCard from '../common/AnimatedCard';
import {
  pageVariants,
  staggerContainerVariants,
  staggerItemVariants,
  fadeInVariants,
} from '../../animations/variants';

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const Dashboard: React.FC = () => {
  const [repositories, setRepositories] = useState<Repository[]>([]);
  const [insights, setInsights] = useState<InsightsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [crawling, setCrawling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [backendDisconnected, setBackendDisconnected] = useState(false);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [reposData, insightsData] = await Promise.all([
        apiService.getRepositories(1, 12), // Increased from 6 to 12 repositories
        apiService.getTrendingInsights(),
      ]);

      setRepositories(reposData);
      setInsights(insightsData);
    } catch (err) {
      let errorMessage = 'Failed to fetch data. Please try again.';
      
      // Check for network errors
      if (err instanceof Error) {
        if (err.message.includes('Network Error') || err.message.includes('CORS')) {
          errorMessage = 'Unable to connect to backend server. The backend may not be running or deployed.';
          setBackendDisconnected(true);
        } else if (err.message.includes('timeout')) {
          errorMessage = 'Request timeout. The server may be slow or unavailable.';
        } else {
          errorMessage = err.message;
        }
      }
      
      setError(errorMessage);
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStartCrawl = async () => {
    try {
      setCrawling(true);
      setError(null);
      setSuccess(null);

      const response = await apiService.startCrawl();

      setSuccess('Crawl started successfully! Data will refresh shortly.');
      console.log('Crawl started successfully:', response);

      setTimeout(() => {
        setSuccess(null);
      }, 5000);

      setTimeout(() => {
        fetchInitialData();
        setCrawling(false);
      }, 3000); // Reduced delay for better UX
    } catch (err) {
      let errorMessage = 'Failed to start crawling. Please try again.';
      
      // Check for network errors
      if (err instanceof Error) {
        if (err.message.includes('Network Error') || err.message.includes('CORS')) {
          errorMessage = 'Unable to connect to backend server. The backend may not be running or deployed.';
          setBackendDisconnected(true);
        } else if (err.message.includes('timeout')) {
          errorMessage = 'Request timeout. The server may be slow or unavailable.';
        } else {
          errorMessage = err.message;
        }
      }
      
      setError(errorMessage);
      console.error('Error starting crawl:', err);
      setCrawling(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  // Check backend connectivity on mount and periodically
  useEffect(() => {
    const checkBackendConnection = async () => {
      try {
        const isConnected = await apiService.isApiAvailable();
        if (!isConnected) {
          setBackendDisconnected(true);
        } else {
          setBackendDisconnected(false);
        }
      } catch (error) {
        console.error('Failed to check backend connection:', error);
        setBackendDisconnected(true);
      }
    };

    checkBackendConnection();
    
    // Set up periodic checks every 30 seconds
    const interval = setInterval(checkBackendConnection, 30000);
    
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 8 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          >
            <RefreshIcon sx={{ fontSize: 48, color: '#00D4FF' }} />
          </motion.div>
        </Box>
      </Container>
    );
  }

  // Show empty state when no data has been fetched yet
  if (repositories.length === 0 && !insights) {
    return (
      <motion.div
        variants={pageVariants}
        initial="initial"
        animate="animate"
        exit="exit"
      >
        <Container maxWidth="xl" sx={{ py: 8 }}>
          {/* Header Section */}
          <motion.div
            variants={fadeInVariants}
            initial="hidden"
            animate="visible"
            style={{ marginBottom: '4rem', marginTop: '2rem' }}
          >
            <Typography
              variant="h2"
              component="h1"  
              sx={{
                textAlign: 'center',
                background: 'linear-gradient(135deg, #00D4FF 0%, #FF00FF 100%)',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                fontFamily: '"Orbitron", sans-serif',
                fontWeight: 700,
              }}
            >
              GitHub Repository Analytics
            </Typography>
            <Typography
              variant="h6"
              component="p"
              sx={{
                textAlign: 'center',
                color: '#B0B0B0',
                maxWidth: '600px',
                mx: 'auto',
              }}
            >
              Discover trending repositories and AI-powered insights from the world's largest code repository
            </Typography>
          </motion.div>

          {/* Control Panel */}
          <motion.div
            variants={fadeInVariants}
            initial="hidden"
            animate="visible"
            transition={{ delay: 0.2 }}
            style={{ marginBottom: '2rem', textAlign: 'center' }}
          >
            <motion.button
              onClick={handleStartCrawl}
              disabled={crawling}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              style={{
                color: '#00D4FF',
                fontFamily: '"Orbitron", sans-serif',
                fontWeight: 600,
                fontSize: '1.1rem',
                padding: '12px 32px',
                borderRadius: '12px',
                border: '2px solid #00D4FF',
                backgroundColor: 'transparent',
                cursor: crawling ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 20px rgba(0, 212, 255, 0.3)',
              }}
              onMouseEnter={(e) => {
                if (!crawling) {
                  e.currentTarget.style.borderColor = '#FF00FF';
                  e.currentTarget.style.color = '#FF00FF';
                  e.currentTarget.style.boxShadow = '0 12px 40px rgba(0, 212, 255, 0.5)';
                  e.currentTarget.style.backgroundColor = 'rgba(0, 212, 255, 0.1)';
                }
              }}
              onMouseLeave={(e) => {
                if (!crawling) {
                  e.currentTarget.style.borderColor = '#00D4FF';
                  e.currentTarget.style.color = '#00D4FF';
                  e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 212, 255, 0.3)';
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
            >
              {crawling ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <RefreshIcon sx={{ animation: `${spin} 2s linear infinite` }} />
                  Fetching Data...
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <RefreshIcon />
                  Fetch Repository Data
                </Box>
              )}
            </motion.button>
          </motion.div>

          {/* Success Alert */}
          <AnimatePresence>
            {success && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                style={{ marginBottom: '2rem' }}
              >
                <Alert severity="success" onClose={() => setSuccess(null)}>
                  {success}
                </Alert>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Error Alert */}
          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                style={{ marginBottom: '2rem' }}
              >
                <Alert severity="error" onClose={() => setError(null)}>
                  {error}
                </Alert>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Empty State Message */}
          <motion.div
            variants={fadeInVariants}
            initial="hidden"
            animate="visible"
            transition={{ delay: 0.4 }}
            style={{ textAlign: 'center', marginTop: '4rem' }}
          >
            <Typography
              variant="h5"
              component="p"
              sx={{
                color: '#B0B0B0',
                mb: 2,
              }}
            >
              {backendDisconnected ? 'Backend Server Unavailable' : 'No data available yet'}
            </Typography>
            <Typography
              variant="body1"
              component="p"
              sx={{
                color: '#808080',
                maxWidth: '500px',
                mx: 'auto',
              }}
            >
              {backendDisconnected 
                ? 'The backend server is not running or deployed. This demo shows the frontend interface only. To see full functionality, the backend server needs to be deployed on a paid hosting service.'
                : 'Click the "Fetch Repository Data" button above to load the latest GitHub repository analytics and insights.'
              }
            </Typography>
          </motion.div>
        </Container>
      </motion.div>
    );
  }

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      <Container maxWidth="xl" sx={{ py: 8 }}>
        {/* Header Section */}
        <motion.div
          variants={fadeInVariants}
          initial="hidden"
          animate="visible"
          style={{ marginBottom: '4rem', marginTop: '2rem' }}
        >
          <Typography
            variant="h2"
            component="h1"
            gutterBottom
            sx={{
              textAlign: 'center',
              background: 'linear-gradient(135deg, #00D4FF 0%, #FF00FF 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              fontFamily: '"Orbitron", sans-serif',
              fontWeight: 700,
            }}
          >
            GitHub Repository Analytics
          </Typography>
          <Typography
            variant="h6"
            component="p"
            sx={{
              textAlign: 'center',
              color: '#B0B0B0',
              maxWidth: '600px',
              mx: 'auto',
            }}
          >
            Discover trending repositories and AI-powered insights from the world's largest code repository
          </Typography>
        </motion.div>

        {/* Control Panel */}
        <motion.div
          variants={fadeInVariants}
          initial="hidden"
          animate="visible"
          transition={{ delay: 0.2 }}
          style={{ marginBottom: '2rem', textAlign: 'center' }}
        >
          <motion.button
            onClick={handleStartCrawl}
            disabled={crawling}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            style={{
              color: '#00D4FF',
              fontFamily: '"Orbitron", sans-serif',
              fontWeight: 600,
              fontSize: '1.1rem',
              padding: '12px 32px',
              borderRadius: '12px',
              border: '2px solid #00D4FF',
              backgroundColor: 'transparent',
              cursor: crawling ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 20px rgba(0, 212, 255, 0.3)',
            }}
            onMouseEnter={(e) => {
              if (!crawling) {
                e.currentTarget.style.borderColor = '#FF00FF';
                e.currentTarget.style.color = '#FF00FF';
                e.currentTarget.style.boxShadow = '0 12px 40px rgba(0, 212, 255, 0.5)';
                e.currentTarget.style.backgroundColor = 'rgba(0, 212, 255, 0.1)';
              }
            }}
            onMouseLeave={(e) => {
              if (!crawling) {
                e.currentTarget.style.borderColor = '#00D4FF';
                e.currentTarget.style.color = '#00D4FF';
                e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 212, 255, 0.3)';
                e.currentTarget.style.backgroundColor = 'transparent';
              }
            }}
          >
            {crawling ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <RefreshIcon sx={{ animation: `${spin} 2s linear infinite` }} />
                Refreshing Data...
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <RefreshIcon />
                Refresh Data
              </Box>
            )}
          </motion.button>
        </motion.div>

        {/* Success Alert */}
        <AnimatePresence>
          {success && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              style={{ marginBottom: '2rem' }}
            >
              <Alert severity="success" onClose={() => setSuccess(null)}>
                {success}
              </Alert>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Error Alert */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              style={{ marginBottom: '2rem' }}
            >
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Main Content Grid */}
        <Grid container spacing={{ xs: 2, md: 4 }}>
          {/* Top Repositories */}
          <Grid size={{ xs: 12, lg: 8 }}>
            <motion.div
              variants={staggerContainerVariants}
              initial="hidden"
              animate="visible"
            >
              <Typography
                variant="h4"
                component="h2"
                gutterBottom
                sx={{
                  fontFamily: '"Orbitron", sans-serif',
                  color: '#FFFFFF',
                  mb: 3,
                }}
              >
                Top Repositories
              </Typography>
              <Grid container spacing={{ xs: 2, md: 3 }}>
                {repositories.map((repo, index) => (
                  <Grid size={{ xs: 12 }} key={repo.id}>
                    <motion.div variants={staggerItemVariants}>
                      <AnimatedCard delay={index * 0.1}>
                        <Box sx={{ p: 3 }}>
                          <Typography
                            variant="h6"
                            component="h3"
                            sx={{
                              fontFamily: '"Orbitron", sans-serif',
                              color: '#00D4FF',
                              mb: 2,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {repo.name_with_owner}
                          </Typography>
                          
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <StarIcon sx={{ color: '#FFD700', fontSize: 20 }} />
                            <Typography variant="body2" sx={{ color: '#FFFFFF' }}>
                              {formatNumber(repo.stargazer_count)} stars
                            </Typography>
                          </Box>

                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <CodeIcon sx={{ color: '#FF00FF', fontSize: 20 }} />
                            <Typography variant="body2" sx={{ color: '#B0B0B0' }}>
                              Created {formatDate(repo.created_at)}
                            </Typography>
                          </Box>

                          <Chip
                            label={`${formatNumber(repo.stargazer_count)} ⭐`}
                            size="small"
                            sx={{
                              background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                              color: '#000000',
                              fontWeight: 600,
                            }}
                          />
                        </Box>
                      </AnimatedCard>
                    </motion.div>
                  </Grid>
                ))}
              </Grid>
            </motion.div>
          </Grid>

          {/* AI Insights Panel */}
          <Grid size={{ xs: 12, lg: 4 }}>
            <motion.div
              variants={staggerContainerVariants}
              initial="hidden"
              animate="visible"
            >
              <Typography
                variant="h4"
                component="h2"
                gutterBottom
                sx={{
                  fontFamily: '"Orbitron", sans-serif',
                  color: '#FFFFFF',
                  mb: 3,
                }}
              >
                AI Insights
              </Typography>
              
              {insights?.items.map((insight, index) => (
                <motion.div 
                  key={index} 
                  variants={staggerItemVariants}
                  style={{ marginBottom: index < insights.items.length - 1 ? '1.5rem' : '0' }}
                >
                  <AnimatedCard delay={index * 0.15} glow={true}>
                    <Box sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <TrendingIcon sx={{ color: '#00FF88', fontSize: 24 }} />
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontFamily: '"Orbitron", sans-serif',
                            color: '#00FF88',
                            fontWeight: 600,
                          }}
                        >
                          {insight.repository_name}
                        </Typography>
                      </Box>
                      
                      {insight.trend_description && (
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#FFFFFF',
                            mb: 2,
                            fontStyle: 'italic',
                          }}
                        >
                          {insight.trend_description}
                        </Typography>
                      )}
                      
                      {insight.ai_explanation && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#B0B0B0',
                            display: 'block',
                          }}
                        >
                          {insight.ai_explanation}
                        </Typography>
                      )}
                    </Box>
                  </AnimatedCard>
                </motion.div>
              ))}
            </motion.div>
          </Grid>
        </Grid>
      </Container>

      {/* Backend Disconnection Toast */}
      <Snackbar
        open={backendDisconnected}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        autoHideDuration={null} // Don't auto-hide
      >
        <MuiAlert 
          severity="warning" 
          sx={{ width: '100%' }}
          onClose={() => setBackendDisconnected(false)}
        >
          Backend isn't deployed because it requires paid server
        </MuiAlert>
      </Snackbar>
    </motion.div>
  );
};

export default Dashboard;